import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Crown, CheckCircle, ArrowRight, Sparkles, Loader2 } from 'lucide-react';

const FoundingSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [polling, setPolling] = useState(true);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    // Poll for a few seconds to ensure webhook has processed
    // In production, this ensures the user's account is updated
    const timer = setTimeout(() => {
      setPolling(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  if (polling) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <Loader2 className="w-12 h-12 text-emerald-600 animate-spin mx-auto mb-4" />
            <p className="text-lg font-medium">Confirming your payment...</p>
            <p className="text-sm text-gray-600 mt-2">This will just take a moment</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="max-w-2xl">
        <CardHeader className="text-center pb-4">
          <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Crown className="w-12 h-12 text-white" />
          </div>
          <CardTitle className="text-3xl">
            🎉 Welcome, Founding Member!
          </CardTitle>
          <p className="text-gray-600 mt-2">
            You're officially one of the first 100
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-gradient-to-r from-amber-50 to-amber-100 border-2 border-amber-300 rounded-lg p-6 text-center">
            <p className="text-2xl font-bold text-amber-900 mb-2">
              $79/year locked in forever!
            </p>
            <p className="text-amber-700">
              (You're saving $149/year vs standard pricing)
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="font-bold text-lg">🚀 What's Next?</h3>
            <ol className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center font-bold text-xs">
                  1
                </span>
                <div>
                  <p className="font-medium">Check your email</p>
                  <p className="text-gray-600">We sent you a welcome email with all the details</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center font-bold text-xs">
                  2
                </span>
                <div>
                  <p className="font-medium">Log in to your dashboard</p>
                  <p className="text-gray-600">Full access is now enabled for your account</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center font-bold text-xs">
                  3
                </span>
                <div>
                  <p className="font-medium">Connect your bank & import data</p>
                  <p className="text-gray-600">Get started by syncing your transactions</p>
                </div>
              </li>
            </ol>
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <h4 className="font-bold text-emerald-900 mb-2">🎁 Your Founding Member Perks</h4>
            <ul className="space-y-1 text-sm text-emerald-800">
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                $79/year forever (never increases)
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Priority email support
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Early access to new features
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Founding Member badge
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Influence on product roadmap
              </li>
            </ul>
          </div>

          <Button
            onClick={() => navigate('/login')}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg"
          >
            Go to Dashboard
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>

          <p className="text-center text-sm text-gray-600">
            <strong>Thank you for believing in us!</strong> 🙏<br />
            We're excited to have you as a founding member.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default FoundingSuccess;
